import React, { createContext, FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { cloneDeep, groupBy, maxBy, orderBy } from "lodash";
import { BenchmarkingDashboardDetailsTypeEnum } from "@app/overviews/network-benchmarking-dashboard/dashboard-details";
import { getPcaLineOfBusiness } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/utility";
import { MedicareNetwork, MedicareParentOrganization } from "@app/overviews/network-benchmarking-dashboard/medicare/filters";
import { NetworkComparisonFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters";
import {
  EffectivenessAndEfficiencyData,
  HospitalAndPcpCountData,
  MarketPenetrationData,
  NetworkCompositionData,
  ProvidersByNetworkAndVolumeData,
  ProvidersByNetworkData,
  TotalAndMarketPenetrationTabTypeEnum,
} from "@app/overviews/network-benchmarking-dashboard/tiles/shared";
import { hospitalAndPcpColors } from "@app/overviews/network-benchmarking-dashboard/tiles/utility";
import { ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { EffectivenessEfficiencyValueEnum } from "@app/projects/servicing-provider";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { AffiliationsData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared/AffiliationsData";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import { BenchmarkingSpecialtyGroup } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared/BenchmarkingSpecialtyGroup";
import { NetworkComparisonFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared/NetworkComparisonFiltersPayload";
import { NetworkComparisonReportFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared/NetworkComparisonReportFiltersPayload";
import { BenchmarkingLineOfBusinessEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import { BenchmarkingNetworkType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/benchmarkingNetworkType";
import { BenchmarkingParentOrganizationType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/benchmarkingParentOrganizationType";
import { QHPNetwork } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPNetwork";
import { MedicarePrimaryFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/medicare/filters/shared/MedicarePrimaryFiltersPayload";
import { ProviderOverlapData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared/ProviderOverlapData";
import { QHPParentOrganization } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPParentOrganization";
import { MedicaidParentOrganization } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/MedicaidParentOrganization";
import { MedicaidNetwork } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/MedicaidNetwork";
import { useAffiliationsVersion } from "@app/overviews/network-benchmarking-dashboard/hooks/useAffiliationsVersion";
import { useEffectivenessAndEfficiencyData } from "@app/overviews/network-benchmarking-dashboard/hooks/useEffectivenessAndEfficiencyData";
import { useHospitalAndPcpCountData } from "@app/overviews/network-benchmarking-dashboard/hooks/useHospitalAndPcpCountData";
import { useMarketPenetrationData } from "@app/overviews/network-benchmarking-dashboard/hooks/useMarketPenetrationData";
import { useProvidersByNetworkAndVolumeData } from "@app/overviews/network-benchmarking-dashboard/hooks/useProvidersByNetworkAndVolumeData";
import { useProviderOverlapData } from "@app/overviews/network-benchmarking-dashboard/hooks/useProviderOverlapData";
import { useProvidersByNetworkData } from "@app/overviews/network-benchmarking-dashboard/hooks/useProvidersByNetworkData";
import { useAffiliationsData } from "@app/overviews/network-benchmarking-dashboard/hooks/useAffiliationsData";

export interface BenchmarkingContextValues {
  affiliationsData: AffiliationsData[];
  affiliationsDataLoading: boolean;
  affiliationsVersion: number;
  affiliationsVersionLoading: boolean;
  dashboardDetailsTotalCount: number;
  effectivenessAndEfficiencyData: EffectivenessAndEfficiencyData[];
  effectivenessAndEfficiencyDataLoading: boolean;
  fullMarketPenetrationData: MarketPenetrationData[];
  fullMarketPenetrationDataLoading: boolean;
  getFormattedFiltersPayload: (payload: NetworkComparisonFiltersPayload) => NetworkComparisonFiltersPayload;
  getFormattedReportFiltersPayload: (payload: NetworkComparisonFiltersPayload) => NetworkComparisonReportFiltersPayload;
  hContractColorMap: Map<string, string>;
  hospitalAndPcpCountData: HospitalAndPcpCountData[];
  hospitalAndPcpCountDataLoading: boolean;
  networkCompositionData: NetworkCompositionData[];
  networkCompositionDataLoading: boolean;
  onApplyNetworkComparisonFiltersClick: (networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, isFirstLoad?: boolean) => void;
  onContractAffiliationsClick: (selectedAffiliationsContract: BenchmarkingNetworkType) => void;
  onDashboardDetailsViewClick: (
    drillinOption: BenchmarkingDashboardDetailsTypeEnum,
    clickedCount: number,
    selectedVersionId?: number,
    networkOverlappingCount?: number,
    volumeLabel?: ClaimsActivityVolumeLabelEnum,
    isHospitalAndPcp?: boolean,
    professionalContractEntityId?: number,
  ) => void;
  onSubmitNetworkFiltersClick: (parentOrganizations: BenchmarkingParentOrganizationType[]) => void;
  providerOverlapData: ProviderOverlapData;
  providerOverlapDataLoading: boolean;
  providersByNetworkData: ProvidersByNetworkData[];
  providersByNetworkDataLoading: boolean;
  selectedAffiliationsNetwork: BenchmarkingNetworkType;
  setShowDashboardDetails: (showDashboardDetails: boolean) => void;
  setTotalAndMarketPenetrationTab: (tab: TotalAndMarketPenetrationTabTypeEnum) => void;
  showDashboardDetails: boolean;
  totalAndMarketPenetrationTab: TotalAndMarketPenetrationTabTypeEnum;
  totalProviderCount: ProvidersByNetworkData[];
}

const initialValues: BenchmarkingContextValues = {
  affiliationsData: [],
  affiliationsDataLoading: false,
  affiliationsVersion: null,
  affiliationsVersionLoading: false,
  dashboardDetailsTotalCount: null,
  effectivenessAndEfficiencyData: [],
  effectivenessAndEfficiencyDataLoading: false,
  fullMarketPenetrationData: [],
  fullMarketPenetrationDataLoading: false,
  getFormattedFiltersPayload: () => null,
  getFormattedReportFiltersPayload: () => null,
  hContractColorMap: new Map<string, string>(),
  hospitalAndPcpCountData: [],
  hospitalAndPcpCountDataLoading: false,
  networkCompositionData: [],
  networkCompositionDataLoading: false,
  onApplyNetworkComparisonFiltersClick: () => null,
  onContractAffiliationsClick: () => null,
  onDashboardDetailsViewClick: () => null,
  onSubmitNetworkFiltersClick: () => null,
  providerOverlapData: null,
  providerOverlapDataLoading: false,
  providersByNetworkData: [],
  providersByNetworkDataLoading: false,
  selectedAffiliationsNetwork: null,
  setShowDashboardDetails: () => null,
  setTotalAndMarketPenetrationTab: () => null,
  showDashboardDetails: false,
  totalAndMarketPenetrationTab: TotalAndMarketPenetrationTabTypeEnum.ProviderCount,
  totalProviderCount: [],
}

export const BenchmarkingContext = createContext(initialValues);

// rolls providers-by-network-and-volume counts up into a per-contract percentage breakdown (network composition),
// once both of the queries it combines have returned data
const buildNetworkCompositionData = (
  providersByNetworkAndVolumeData: ProvidersByNetworkAndVolumeData[],
  providersByNetworkData: ProvidersByNetworkData[]
): NetworkCompositionData[] => {
  if (!providersByNetworkAndVolumeData.length || !providersByNetworkData.length) {
    return [];
  }

  const formattedNetworkCompositionData: NetworkCompositionData[] = [];
  // the rows below are written to, and the query's cached data must stay untouched, so work on a copy
  const groupedByContractId = groupBy(cloneDeep(providersByNetworkAndVolumeData), "contractId");

  Object.entries(groupedByContractId).forEach((datum) => {
    const providersByNetworkDatum: ProvidersByNetworkData = providersByNetworkData.find(x => x.contractId === datum[0]);

    if (!!providersByNetworkDatum) {
      for (const x of datum[1]) {
        if (!!x.providerCount && !!providersByNetworkDatum?.providerCount) {
          const unroundedPercentage = (x.providerCount / providersByNetworkDatum.providerCount) * 100;
          const roundedPercentage = Math.round(unroundedPercentage * 10) / 10;

          // if there are values and the rounded percentage rounded to 0, display the unrounded percentage (example: 0.007%)
          x.percentage = roundedPercentage === 0 ? unroundedPercentage : roundedPercentage;
          x.displayPercentage = roundedPercentage === 0 ? unroundedPercentage : roundedPercentage;
        }
      }

      // if the display value is smaller than the minimum display percentage (2%),
      // then increase the percentage by subtracting however much more is needed to reach the 2%
      // from whatever volume label has the highest display percentage
      for (const x of datum[1]) {
        const sorted: ProvidersByNetworkAndVolumeData[] = orderBy(datum[1], "displayPercentage", "desc");

        if (x.displayPercentage < 2) {
          const amount = 2 - x.displayPercentage;
          sorted[0].displayPercentage = sorted[0].displayPercentage - amount;
          x.displayPercentage = x.displayPercentage + amount;
        }
      }

      const networkCompositionData: NetworkCompositionData = {
        contractId: datum[1][0].contractId,
        totalProviderCount: providersByNetworkDatum.providerCount,
        versionId: datum[1][0].versionId,
      };

      for (const x of datum[1]) {
        networkCompositionData[`${x.volumeIndicator}DisplayPercentage`] = x.displayPercentage;
        networkCompositionData[`${x.volumeIndicator}Percentage`] = x.percentage;
        networkCompositionData[`${x.volumeIndicator}Count`] = x.providerCount;
      }

      formattedNetworkCompositionData.push(networkCompositionData);
    }
  });

  return formattedNetworkCompositionData;
}

// combines market penetration with providers-by-network counts, since the widget divides the network composition
// count by the providers-by-network count
const buildFullMarketPenetrationData = (
  marketPenetrationData: MarketPenetrationData[],
  providersByNetworkData: ProvidersByNetworkData[]
): MarketPenetrationData[] => {
  if (!marketPenetrationData.length || !providersByNetworkData.length) {
    return [];
  }

  // the rows below are written to, and the query's cached data must stay untouched, so work on a copy
  const fullMarketPenetrationData = cloneDeep(marketPenetrationData);

  for (const marketPenetrationDatum of fullMarketPenetrationData) {
    const providersByNetworkDatum = providersByNetworkData.find(x => x.contractId === marketPenetrationDatum.contractId);

    if (!!providersByNetworkDatum) {
      marketPenetrationDatum.inNetworkOnlyCount = providersByNetworkDatum.providerCount - marketPenetrationDatum.networkMarketOverlapCount;
      const unroundedPercentage = (marketPenetrationDatum.inNetworkOnlyCount / (marketPenetrationDatum.networkMarketOverlapCount + marketPenetrationDatum.universeProviderCount)) * 100;
      const roundedPercentage = Math.round(unroundedPercentage * 10) / 10;
      // if there are values and the rounded percentage rounded to 0, display the unrounded percentage (example: 0.007%)
      marketPenetrationDatum.inNetworkOnlyPercentage = roundedPercentage === 0 ? unroundedPercentage : roundedPercentage;
      marketPenetrationDatum.inNetworkOnlyDisplayPercentage = marketPenetrationDatum.inNetworkOnlyPercentage < 2 ? 2 : marketPenetrationDatum.inNetworkOnlyPercentage;
    }
  }

  return fullMarketPenetrationData;
}

const BenchmarkingContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  const [hContractColorMap, setHContractColorMap] = useState<Map<string, string>>(initialValues.hContractColorMap);
  const [selectedAffiliationsNetwork, setSelectedAffiliationsNetwork] = useState<BenchmarkingNetworkType>(initialValues.selectedAffiliationsNetwork);
  const [showDashboardDetails, setShowDashboardDetails] = useState<boolean>(initialValues.showDashboardDetails);
  const [totalAndMarketPenetrationTab, setTotalAndMarketPenetrationTab] = useState<TotalAndMarketPenetrationTabTypeEnum>(initialValues.totalAndMarketPenetrationTab);
  const [dashboardDetailsTotalCount, setDashboardDetailsTotalCount] = useState<number>(initialValues.dashboardDetailsTotalCount);
  const [totalProviderCount, setTotalProviderCount] = useState<ProvidersByNetworkData[]>(initialValues.totalProviderCount);

  // the applied payloads driving each query below; set by the click handlers further down instead of imperatively
  // calling a service method, so the dashboard's seven data fetches can go through tanstack query
  const [networkComparisonFiltersPayload, setNetworkComparisonFiltersPayload] = useState<NetworkComparisonFiltersPayload | null>(null);
  const [hospitalAndPcpFiltersPayload, setHospitalAndPcpFiltersPayload] = useState<NetworkComparisonFiltersPayload | null>(null);
  const [affiliationsFiltersPayload, setAffiliationsFiltersPayload] = useState<NetworkComparisonFiltersPayload | null>(null);

  // the line of business the payloads above were applied under. service changes on the render a line of business switch
  // happens, but the payloads are only cleared by an effect afterwards, so queries are only given a payload that
  // belongs to the current service's line of business
  const [appliedLineOfBusiness, setAppliedLineOfBusiness] = useState<BenchmarkingLineOfBusinessEnum | null>(null);

  // set alongside networkComparisonFiltersPayload only when a network submission (not a plain filter apply) triggered
  // it, so totalProviderCount only snapshots providersByNetworkData right after the networks themselves change
  const captureTotalProviderCountRef = useRef(false);

  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const networkComparisonFiltersContext = useContext(NetworkComparisonFiltersContext);
  const featureFlagContext = useContext(FeatureFlagContext);
  const service = benchmarkingFiltersContext.getService();
  const isPayloadCurrent = appliedLineOfBusiness === service?.lineOfBusiness;
  const currentNetworkComparisonFiltersPayload = isPayloadCurrent ? networkComparisonFiltersPayload : null;
  const currentHospitalAndPcpFiltersPayload = isPayloadCurrent ? hospitalAndPcpFiltersPayload : null;
  const currentAffiliationsFiltersPayload = isPayloadCurrent ? affiliationsFiltersPayload : null;

  const { affiliationsVersion, affiliationsVersionLoading } = useAffiliationsVersion(featureFlagContext.enabledFeatureFlags);
  const { effectivenessAndEfficiencyData, effectivenessAndEfficiencyDataLoading } = useEffectivenessAndEfficiencyData(service, currentNetworkComparisonFiltersPayload);
  const { hospitalAndPcpCountData, hospitalAndPcpCountDataLoading } = useHospitalAndPcpCountData(service, currentHospitalAndPcpFiltersPayload);
  const { marketPenetrationData, marketPenetrationDataLoading } = useMarketPenetrationData(service, currentNetworkComparisonFiltersPayload);
  const { providersByNetworkAndVolumeData, providersByNetworkAndVolumeDataLoading } = useProvidersByNetworkAndVolumeData(service, currentNetworkComparisonFiltersPayload);
  const { providerOverlapData, providerOverlapDataLoading } = useProviderOverlapData(service, currentNetworkComparisonFiltersPayload);
  const { providersByNetworkData, providersByNetworkDataLoading } = useProvidersByNetworkData(service, currentNetworkComparisonFiltersPayload);
  const { affiliationsData, affiliationsDataLoading } = useAffiliationsData(
    service,
    currentAffiliationsFiltersPayload,
    benchmarkingFiltersContext.isAffiliationState(),
    benchmarkingFiltersContext.selectedNetworks
  );

  const fullMarketPenetrationData = useMemo(
    () => buildFullMarketPenetrationData(marketPenetrationData, providersByNetworkData),
    [marketPenetrationData, providersByNetworkData]
  );
  const fullMarketPenetrationDataLoading = marketPenetrationDataLoading || providersByNetworkDataLoading;

  const networkCompositionData = useMemo(
    () => buildNetworkCompositionData(providersByNetworkAndVolumeData, providersByNetworkData),
    [providersByNetworkAndVolumeData, providersByNetworkData]
  );
  const networkCompositionDataLoading = providersByNetworkAndVolumeDataLoading || providersByNetworkDataLoading;

  useEffect(() => {
    if (captureTotalProviderCountRef.current && !providersByNetworkDataLoading) {
      setTotalProviderCount(providersByNetworkData);
      captureTotalProviderCountRef.current = false;
    }
  }, [providersByNetworkData, providersByNetworkDataLoading]);

  useEffect(() => {
    if (!benchmarkingFiltersContext.displayedNetworkFilters.length || !benchmarkingFiltersContext.displayedPrimaryFilters) {
      setNetworkComparisonFiltersPayload(null);
      setHospitalAndPcpFiltersPayload(null);
      setTotalProviderCount([]);

      if (benchmarkingFiltersContext.isAffiliationState()) {
        setAffiliationsFiltersPayload(null);
      }
    }
  }, [benchmarkingFiltersContext.displayedNetworkFilters, benchmarkingFiltersContext.displayedPrimaryFilters, featureFlagContext.enabledFeatureFlags]);

  const getFormattedFiltersPayload = useCallback((payload: NetworkComparisonFiltersPayload): NetworkComparisonFiltersPayload => {
    // if all options are selected, send empty arrays in order to save resources
    if (payload.volumeIndicators.length === networkComparisonFiltersContext.mappedVolumeIndicators.length) {
      payload.volumeIndicators = [];
    }

    if (payload.effectivenessRanks.length === networkComparisonFiltersContext.mappedEffectivenessAndEfficiencyValues.length) {
      payload.effectivenessRanks = [];
    }

    if (payload.efficiencyRanks.length === networkComparisonFiltersContext.mappedEffectivenessAndEfficiencyValues.length) {
      payload.efficiencyRanks = [];
    }

    if (payload.specialtyGroupIds.length === benchmarkingFiltersContext.rawSpecialties.length) {
      payload.specialtyGroupIds = [];
    }

    return payload;
  }, [
    networkComparisonFiltersContext.mappedVolumeIndicators.length,
    networkComparisonFiltersContext.mappedEffectivenessAndEfficiencyValues.length,
    benchmarkingFiltersContext.rawSpecialties.length,
  ]);

  const getFormattedReportFiltersPayload = (payload: NetworkComparisonFiltersPayload): NetworkComparisonReportFiltersPayload  => {
    const reportPayload = {
      ...payload,
      contractStarRating: benchmarkingFiltersContext.displayedPrimaryFilters.starRatings,
      membershipCount: (benchmarkingFiltersContext.displayedPrimaryFilters as MedicarePrimaryFiltersPayload).membershipCount,
      planType: benchmarkingFiltersContext.displayedPrimaryFilters.planTypes,
      snpPlan: (benchmarkingFiltersContext.displayedPrimaryFilters as MedicarePrimaryFiltersPayload).snpFilter,
      state: { ...benchmarkingFiltersContext.selectedState, counties: [] },
    } as NetworkComparisonReportFiltersPayload;
    return reportPayload;
  }

  // if a contract is not being selected on click, then default the selected affiliations network to the primary network
  const applyAffiliationsFiltersPayload = (payload: NetworkComparisonFiltersPayload): void => {
    const affiliationsPayload: NetworkComparisonFiltersPayload = {
      ...payload,
      selectedAffiliationsNetwork: payload.selectedAffiliationsNetwork ?? payload.primaryNetwork,
    };

    setSelectedAffiliationsNetwork(affiliationsPayload.selectedAffiliationsNetwork);
    setAffiliationsFiltersPayload(affiliationsPayload);
    setAppliedLineOfBusiness(service?.lineOfBusiness ?? null);
  }

  const onApplyNetworkComparisonFiltersClick = (payload: NetworkComparisonFiltersPayload, isFirstLoad?: boolean): void => {
    if (totalAndMarketPenetrationTab === TotalAndMarketPenetrationTabTypeEnum.NetworkBreadth) {
      setTotalAndMarketPenetrationTab(TotalAndMarketPenetrationTabTypeEnum.ProviderCount);
    }

    const formattedPayload = getFormattedFiltersPayload(cloneDeep(payload));

    if (isFirstLoad) {
      captureTotalProviderCountRef.current = true;
    }

    setNetworkComparisonFiltersPayload(formattedPayload);
    applyAffiliationsFiltersPayload(formattedPayload);
  }

  const onContractAffiliationsClick = (selectedAffiliationsContract: BenchmarkingNetworkType) => {
    const payload = getFormattedFiltersPayload(cloneDeep(networkComparisonFiltersContext.displayedNetworkComparisonFilters));
    payload.selectedAffiliationsNetwork = selectedAffiliationsContract;
    applyAffiliationsFiltersPayload(payload);
  }

  const onDashboardDetailsViewClick = (
    drillinOption: BenchmarkingDashboardDetailsTypeEnum,
    clickedCount: number,
    selectedVersionId?: number,
    networkOverlappingCount?: number,
    volumeLabel?: ClaimsActivityVolumeLabelEnum,
    isHospitalAndPcp?: boolean,
    professionalContractEntityId?: number,
  ): void => {
    const filters: NetworkComparisonFiltersPayload = cloneDeep(networkComparisonFiltersContext.displayedNetworkComparisonFilters);
    filters.drillinOption = drillinOption;
    filters.selectedVersionId = selectedVersionId;
    filters.networkOverlappingCount = networkOverlappingCount;
    filters.lineOfBusiness = benchmarkingFiltersContext.selectedLineOfBusiness;
    filters.professionalContractEntityId = professionalContractEntityId;

    if (!!volumeLabel) {
      filters.volumeIndicators = [volumeLabel];
    }

    if (isHospitalAndPcp) {
      const specialties: number[] = [];
      const hospital: BenchmarkingSpecialtyGroup = benchmarkingFiltersContext.rawSpecialties.find(x => x.isAcuteInPatientHospital);
      const pcp: BenchmarkingSpecialtyGroup = benchmarkingFiltersContext.rawSpecialties.find(x => x.isPCP);

      if (!!hospital) {
        specialties.push(hospital.specialtyGroupId);
      }

      if (!!pcp) {
        specialties.push(pcp.specialtyGroupId)
      }

      filters.specialtyGroupIds = specialties;
    }

    networkComparisonFiltersContext.setDisplayedNetworkComparisonFilters(filters);
    setShowDashboardDetails(true);
    setDashboardDetailsTotalCount(clickedCount);
  }

  const onSubmitNetworkFiltersClick = (parentOrganizations: BenchmarkingParentOrganizationType[]): void => {
    const selectedHContracts: BenchmarkingNetworkType[] = cloneDeep(benchmarkingFiltersContext.selectedNetworks);
    const hContractIds: string[] = selectedHContracts.map(x => x.contractId);
    const newHContractColorMap: Map<string, string> = new Map<string, string>();

    hContractIds.forEach((id, index) => {
      newHContractColorMap.set(id, Object.values(hospitalAndPcpColors)[index % 5]);
    });

    // if any of your QES networks are selected, the primary network should default to your QES network with the highest membership count
    // if none of your QES networks are selected, the primary network should default to the network with the highest membership
    let yourNetworks: BenchmarkingNetworkType[] = [];

    switch (benchmarkingFiltersContext.displayedPrimaryFilters.lineOfBusinessId) {
      case BenchmarkingLineOfBusinessEnum.Marketplace:
        yourNetworks = (parentOrganizations as QHPParentOrganization[]).find(x => x.parentOrganizationName === "Your QES Networks")?.hContracts;
        break;
      case BenchmarkingLineOfBusinessEnum.Medicare:
        yourNetworks = (parentOrganizations as MedicareParentOrganization[]).find(x => x.parentOrganizationName === "Your QES Networks")?.hContracts;
        break;
      case BenchmarkingLineOfBusinessEnum.Medicaid:
        const medicaidHContract = (parentOrganizations as MedicaidParentOrganization[]).find(x => x.parentOrganizationName === "Your QES Networks")?.hContract;

        if (!!medicaidHContract) {
          yourNetworks = [medicaidHContract];
        }
        break;
      default:
        break;
    }

    const selectedYourNetworks: BenchmarkingNetworkType[] = selectedHContracts.filter(y => yourNetworks.map(x => x.contractId).includes(y.contractId));
    const networks: BenchmarkingNetworkType[] = !!selectedYourNetworks.length ? selectedYourNetworks : selectedHContracts;
    let primaryNetwork: BenchmarkingNetworkType;

    switch (benchmarkingFiltersContext.displayedPrimaryFilters.lineOfBusinessId) {
      case BenchmarkingLineOfBusinessEnum.Marketplace:
        primaryNetwork = orderBy(networks as QHPNetwork[], (x) => x.issuerId, "asc")[0];
        break;
      case BenchmarkingLineOfBusinessEnum.Medicare:
        primaryNetwork = maxBy(networks as MedicareNetwork[], (x) => x.membership);
        break;
      case BenchmarkingLineOfBusinessEnum.Medicaid:
        primaryNetwork = maxBy(networks as MedicaidNetwork[], (x) => x.membershipCount);
        break;
      default:
        break;
    }

    const filteredHContracts: BenchmarkingNetworkType[] = selectedHContracts.filter(x => x.contractId !== primaryNetwork.contractId);

    const networkComparisonFiltersPayloadToApply: NetworkComparisonFiltersPayload = {
      comparisonNetworks: filteredHContracts,
      countyIds: benchmarkingFiltersContext.displayedPrimaryFilters.countyIds,
      effectivenessRanks: Object.values(EffectivenessEfficiencyValueEnum),
      efficiencyRanks: Object.values(EffectivenessEfficiencyValueEnum),
      marketProjectId: benchmarkingFiltersContext.marketProjectId,
      pcaLineOfBusiness: getPcaLineOfBusiness(benchmarkingFiltersContext.selectedLineOfBusiness),
      primaryNetwork,
      programKey: benchmarkingFiltersContext.programKey,
      specialtyGroupIds: benchmarkingFiltersContext.rawSpecialties.map(x => x.specialtyGroupId),
      state: { ...benchmarkingFiltersContext.selectedState, counties: [] },
      volumeIndicators: Object.values(ClaimsActivityVolumeLabelEnum).filter(x => x !== ClaimsActivityVolumeLabelEnum.Total),
    };
    const formattedFiltersPayload: NetworkComparisonFiltersPayload = getFormattedFiltersPayload(cloneDeep(networkComparisonFiltersPayloadToApply));

    setHContractColorMap(newHContractColorMap);
    benchmarkingFiltersContext.setDisplayedNetworkFilters(selectedHContracts);
    networkComparisonFiltersContext.getNetworkComparisonFilterData(cloneDeep(networkComparisonFiltersPayloadToApply));
    setHospitalAndPcpFiltersPayload(formattedFiltersPayload);
    setAppliedLineOfBusiness(service?.lineOfBusiness ?? null);
    onApplyNetworkComparisonFiltersClick(formattedFiltersPayload, true);
  }

  return (
    <BenchmarkingContext.Provider
      value={{
        affiliationsData,
        affiliationsDataLoading,
        affiliationsVersion,
        affiliationsVersionLoading,
        dashboardDetailsTotalCount,
        effectivenessAndEfficiencyData,
        effectivenessAndEfficiencyDataLoading,
        fullMarketPenetrationData,
        fullMarketPenetrationDataLoading,
        getFormattedFiltersPayload,
        getFormattedReportFiltersPayload,
        hContractColorMap,
        hospitalAndPcpCountData,
        hospitalAndPcpCountDataLoading,
        networkCompositionData,
        networkCompositionDataLoading,
        onApplyNetworkComparisonFiltersClick,
        onContractAffiliationsClick,
        onDashboardDetailsViewClick,
        onSubmitNetworkFiltersClick,
        providerOverlapData,
        providerOverlapDataLoading,
        providersByNetworkData,
        providersByNetworkDataLoading,
        selectedAffiliationsNetwork,
        setShowDashboardDetails,
        setTotalAndMarketPenetrationTab,
        showDashboardDetails,
        totalAndMarketPenetrationTab,
        totalProviderCount,
      }}>
      {props.children}
    </BenchmarkingContext.Provider>
  )
}

export default BenchmarkingContextProvider;
